import {
    Badge,
    Banner,
    BlockStack,
    Button,
    Card,
    DataTable,
    DatePicker,
    Divider,
    InlineStack,
    Page,
    Pagination,
    Popover,
    Select,
    Spinner,
    Text,
    TextField,
} from '@shopify/polaris';
import { ArrowLeftIcon, CalendarIcon, InfoIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import {
    FitmentCoverageHealthReport,
    FitmentCoverageHealthVehicle,
    StorefrontSearchReport,
    StorefrontSearchReportTopSearch,
} from '../../app/models/Reports';
import {
    SearchActivityChart,
} from './SearchActivitySummary';

type ReportRange = '7' | '30' | '90' | 'custom';
type ComparisonPeriod =
    | 'none'
    | 'previous_period'
    | 'previous_year'
    | 'previous_year_weekday'
    | 'custom';
type ComparisonDateRange = {
    start: Date;
    end: Date;
};
type DailyBreakdownRow = {
    currentDate: string;
    currentSearches: number;
    currentDataAvailable: boolean;
    comparisonDate?: string;
    comparisonSearches?: number;
    comparisonDataAvailable?: boolean;
};
type ReportKind =
    | 'daily_search_volume'
    | 'top_vehicle_searches'
    | 'coverage_opportunities'
    | 'search_demand_trends'
    | 'fitment_coverage_health';

type ReportDefinition = {
    value: ReportKind;
    label: string;
};

const reportDefinitions: ReportDefinition[] = [
    {
        value: 'daily_search_volume',
        label: 'Daily search volume',
    },
    {
        value: 'top_vehicle_searches',
        label: 'Top vehicle searches',
    },
    {
        value: 'coverage_opportunities',
        label: 'Coverage opportunities',
    },
    {
        value: 'search_demand_trends',
        label: 'Search demand trends',
    },
    {
        value: 'fitment_coverage_health',
        label: 'Fitment coverage health',
    },
];

const reportOptions = reportDefinitions.map((definition) => ({
    label: definition.label,
    value: definition.value,
}));

const getInitialReport = (): ReportKind => {
    const requestedReport = new URLSearchParams(window.location.search).get('report');

    // Preserve older dashboard/deep links that pointed to the combined
    // Search activity report before its two views became standalone reports.
    if (requestedReport === 'search_activity') {
        return 'daily_search_volume';
    }

    return reportDefinitions.some((definition) => definition.value === requestedReport)
        ? requestedReport as ReportKind
        : 'daily_search_volume';
};

const rangeOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
    { label: 'Custom range', value: 'custom' },
];

const comparisonOptions = [
    { label: 'No comparison', value: 'none' },
    { label: 'Previous period', value: 'previous_period' },
    { label: 'Previous year', value: 'previous_year' },
    { label: 'Previous year (match day of week)', value: 'previous_year_weekday' },
    { label: 'Custom period', value: 'custom' },
];

const comparisonReportKinds = new Set<ReportKind>([
    'daily_search_volume',
    'top_vehicle_searches',
    'search_demand_trends',
]);

const toInputDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const initialToDate = toInputDate(new Date());
const initialFromDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() - 29);
    return toInputDate(date);
})();

const shiftInputDate = (value: string, days: number) => {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    date.setDate(date.getDate() + days);
    return toInputDate(date);
};

const parseInputDate = (value: string) => {
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? new Date() : date;
};

const formatPickerDate = (date: Date) => date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

const formatComparisonRangeLabel = (from: string, to: string) => {
    const fromValue = parseInputDate(from);
    const toValue = parseInputDate(to);

    return `${fromValue.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })} – ${toValue.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })}`;
};

const initialComparisonToDate = shiftInputDate(initialFromDate, -1);
const initialComparisonFromDate = shiftInputDate(initialComparisonToDate, -29);

const getInputDateRangeDays = (from: string, to: string) => {
    const fromValue = new Date(`${from}T00:00:00`);
    const toValue = new Date(`${to}T00:00:00`);
    if (Number.isNaN(fromValue.getTime()) || Number.isNaN(toValue.getTime())) return 0;
    return Math.round((toValue.getTime() - fromValue.getTime()) / 86400000) + 1;
};

const formatCount = (value?: number) => (value ?? 0).toLocaleString();

type ReportSummaryMetricProps = {
    label: string;
    value: number | string;
    comparisonValue?: number;
    showComparison?: boolean;
};

const ReportSummaryMetric = ({
    label,
    value,
    comparisonValue,
    showComparison = false,
}: ReportSummaryMetricProps) => {
    const numericValue = typeof value === 'number' ? value : undefined;
    const percentChange = showComparison
        && numericValue !== undefined
        && comparisonValue !== undefined
        ? comparisonValue === 0
            ? numericValue === 0 ? 0 : null
            : Math.round(((numericValue - comparisonValue) / comparisonValue) * 100)
        : undefined;
    const direction = percentChange == null || percentChange === 0
        ? ''
        : percentChange > 0 ? '↗' : '↘';
    const changeLabel = percentChange === null
        ? 'New'
        : percentChange === undefined
            ? undefined
            : `${Math.abs(percentChange)}%`;
    const accessibleChange = percentChange === null
        ? 'New compared with the selected comparison period'
        : percentChange === 0
            ? 'No change from the selected comparison period'
            : percentChange === undefined
                ? undefined
                : `${Math.abs(percentChange)} percent ${percentChange > 0 ? 'increase' : 'decrease'} from the selected comparison period`;

    return (
        <div className="pf-report-summary-metric">
            <span className="pf-report-summary-metric__label">{label}</span>
            <span className="pf-report-summary-metric__value-row">
                <strong>{typeof value === 'number' ? formatCount(value) : value}</strong>
                {changeLabel && (
                    <span
                        className="pf-report-summary-metric__change"
                        aria-label={accessibleChange}
                        title="Compared with the selected comparison period"
                    >
                        {direction} {changeLabel}
                    </span>
                )}
            </span>
        </div>
    );
};

const formatDisplayDate = (value?: string) => {
    if (!value) return '-';

    const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return '-';

    return parsed.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const formatDateOnly = (value?: string) => value?.slice(0, 10) || '-';

const isSearchDataAvailableOn = (
    report: StorefrontSearchReport,
    date?: string
) => {
    if (!date) return false;
    // If an older API response does not include an availability boundary, do not
    // assume missing aggregate rows are real zero-search days.
    if (report.dataAvailableFrom === undefined) return false;

    const dataAvailableFrom = report.dataAvailableFrom?.slice(0, 10);
    return Boolean(dataAvailableFrom && date.slice(0, 10) >= dataAvailableFrom);
};

const isComparisonDataComplete = (report?: StorefrontSearchReport) => {
    if (!report?.comparisonEnabled) return true;
    if (report.dataAvailableFrom === undefined) return false;

    const dataAvailableFrom = report.dataAvailableFrom?.slice(0, 10);
    return Boolean(dataAvailableFrom && report.previousFrom.slice(0, 10) >= dataAvailableFrom);
};

const formatFieldName = (key: string) => key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

type VehicleReportTarget = {
    vehicleLabel: string;
    fieldValues: Record<string, string>;
};

const getSearchLabel = (search: VehicleReportTarget) => {
    return search.vehicleLabel?.trim()
        || Object.values(search.fieldValues ?? {}).filter(Boolean).join(' ')
        || '-';
};

const getProductsPathForSearch = (search: VehicleReportTarget) => {
    const fieldValues = Object.entries(search.fieldValues ?? {}).reduce<Record<string, string>>(
        (values, [key, value]) => {
            const normalizedKey = key.trim().toLowerCase();
            const normalizedValue = value?.trim();

            if (normalizedKey && normalizedValue) {
                values[normalizedKey] = normalizedValue;
            }

            return values;
        },
        {}
    );

    if (Object.keys(fieldValues).length === 0) return undefined;

    const params = new URLSearchParams();
    params.set('fieldValues', JSON.stringify(fieldValues));

    // Keep the legacy fields in the URL as well so these links remain compatible
    // with both the standard YMM selector and stores using custom fitment fields.
    ['year', 'make', 'model', 'submodel'].forEach((key) => {
        const value = fieldValues[key];
        if (value) params.set(key, value);
    });

    return `/products?${params.toString()}`;
};

const VehicleProductsLink = ({ search }: { search: VehicleReportTarget }) => {
    const label = getSearchLabel(search);
    const productsPath = getProductsPathForSearch(search);

    if (!productsPath) return <>{label}</>;

    return (
        <Link
            to={productsPath}
            className="pf-report-vehicle-link"
            aria-label={`View products for ${label}`}
            title="View this vehicle in Products"
        >
            {label}
        </Link>
    );
};

const escapeCsvCell = (value: string | number) => {
    let text = String(value ?? '');
    if (/^[=+\-@]/.test(text)) {
        text = `'${text}`;
    }

    return `"${text.replace(/"/g, '""')}"`;
};

const downloadDailyVolumeCsv = (report: StorefrontSearchReport) => {
    const headings = report.comparisonEnabled
        ? ['Date', 'Searches', 'Comparison date', 'Comparison searches']
        : ['Date', 'Searches'];
    const currentCounts = new Map(
        report.dailyCounts.map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const comparisonCounts = new Map(
        (report.previousDailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const periodDays = getInclusiveDayCount(report);
    const rows = Array.from({ length: periodDays }, (_, index) => {
        const currentDate = shiftInputDate(formatDateOnly(report.from), index);
        const comparisonDate = shiftInputDate(formatDateOnly(report.previousFrom), index);
        return [
            currentDate,
            isSearchDataAvailableOn(report, currentDate)
                ? currentCounts.get(currentDate) ?? 0
                : '',
            ...(report.comparisonEnabled
                ? [
                    comparisonDate,
                    isSearchDataAvailableOn(report, comparisonDate)
                        ? comparisonCounts.get(comparisonDate) ?? 0
                        : '',
                ]
                : []),
        ];
    });
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-daily-search-volume-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadTopSearchesCsv = (report: StorefrontSearchReport) => {
    const comparisonComplete = isComparisonDataComplete(report);
    const preferredKeys = ['year', 'make', 'model', 'submodel'];
    const discoveredKeys = Array.from(new Set(
        report.topSearches.flatMap((search) => Object.keys(search.fieldValues ?? {}))
    ));
    const fieldKeys = [
        ...preferredKeys.filter((key) => discoveredKeys.includes(key)),
        ...discoveredKeys.filter((key) => !preferredKeys.includes(key)).sort(),
    ];
    const headings = [
        'Vehicle',
        ...fieldKeys.map(formatFieldName),
        'Searches',
        ...(report.comparisonEnabled
            ? [
                comparisonComplete ? 'Comparison searches' : 'Comparison searches (partial)',
                'Change',
            ]
            : []),
        'Last searched',
    ];
    const rows = report.topSearches.map((search) => [
        getSearchLabel(search),
        ...fieldKeys.map((key) => search.fieldValues?.[key] ?? ''),
        search.searchCount,
        ...(report.comparisonEnabled
            ? [
                search.previousSearchCount,
                comparisonComplete ? search.searchCount - search.previousSearchCount : '',
            ]
            : []),
        formatDateOnly(search.lastSearchedOn),
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-top-vehicle-searches-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadCoverageCsv = (
    report: StorefrontSearchReport,
    opportunities: StorefrontSearchReportTopSearch[]
) => {
    const headings = ['Vehicle', 'Search demand', 'Current mapped products', 'Last searched'];
    const rows = opportunities.map((search) => [
        getSearchLabel(search),
        search.searchCount,
        search.currentMappedProductCount,
        formatDateOnly(search.lastSearchedOn),
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-coverage-opportunities-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadDemandTrendsCsv = (
    report: StorefrontSearchReport,
    trends: StorefrontSearchReportTopSearch[]
) => {
    const comparisonComplete = isComparisonDataComplete(report);
    const headings = report.comparisonEnabled
        ? [
            'Vehicle',
            'Current searches',
            comparisonComplete ? 'Comparison searches' : 'Comparison searches (partial)',
            'Change',
            'Last searched',
        ]
        : ['Vehicle', 'Searches', 'Last searched'];
    const rows = trends.map((search) => [
        getSearchLabel(search),
        search.searchCount,
        ...(report.comparisonEnabled
            ? [
                search.previousSearchCount,
                comparisonComplete ? search.searchCount - search.previousSearchCount : '',
            ]
            : []),
        formatDateOnly(search.lastSearchedOn),
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-search-demand-trends-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadFitmentCoverageCsv = (
    report: FitmentCoverageHealthReport,
    vehicles: FitmentCoverageHealthVehicle[]
) => {
    const headings = ['Vehicle', 'Current mapped products', 'Coverage status'];
    const rows = vehicles.map((vehicle) => [
        getSearchLabel(vehicle),
        vehicle.currentMappedProductCount,
        vehicle.currentMappedProductCount === 0 ? 'No mappings' : 'Low coverage',
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-fitment-coverage-health-${formatDateOnly(report.generatedAt)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const getPercentChange = (current: number, previous: number) => {
    if (previous === 0) return current === 0 ? 0 : null;
    return ((current - previous) / previous) * 100;
};

const formatPercentChange = (current: number, previous: number) => {
    const percent = getPercentChange(current, previous);
    if (percent == null) return 'New';
    const rounded = Math.round(percent);
    return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString()}%`;
};

const getInclusiveDayCount = (report?: StorefrontSearchReport) => {
    if (!report?.from || !report.to) return 30;

    const from = new Date(`${report.from.slice(0, 10)}T00:00:00`);
    const to = new Date(`${report.to.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 30;

    return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1);
};

const buildDailyBreakdownRows = (
    report: StorefrontSearchReport | undefined,
    days: number
): DailyBreakdownRow[] => {
    if (!report?.to || days <= 0) return [];

    const currentCounts = new Map(
        (report.dailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const comparisonCounts = new Map(
        (report.previousDailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const currentEnd = formatDateOnly(report.to);
    const comparisonEnd = formatDateOnly(report.previousTo);

    return Array.from({ length: days }, (_, index) => {
        const offset = -(days - index - 1);
        const currentDate = shiftInputDate(currentEnd, offset);
        const comparisonDate = report.comparisonEnabled
            ? shiftInputDate(comparisonEnd, offset)
            : undefined;

        return {
            currentDate,
            currentSearches: currentCounts.get(currentDate) ?? 0,
            currentDataAvailable: isSearchDataAvailableOn(report, currentDate),
            comparisonDate,
            comparisonSearches: comparisonDate
                ? comparisonCounts.get(comparisonDate) ?? 0
                : undefined,
            comparisonDataAvailable: comparisonDate
                ? isSearchDataAvailableOn(report, comparisonDate)
                : undefined,
        };
    });
};

const ReportsPage = () => {
    const [report, setReport] = useState<StorefrontSearchReport>();
    const [coverageHealth, setCoverageHealth] = useState<FitmentCoverageHealthReport>();
    const [selectedReport, setSelectedReport] = useState<ReportKind>(getInitialReport);
    const [range, setRange] = useState<ReportRange>('30');
    const [appliedRange, setAppliedRange] = useState<ReportRange>('30');
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);
    const [comparison, setComparison] = useState<ComparisonPeriod>('previous_period');
    const [appliedComparison, setAppliedComparison] = useState<ComparisonPeriod>('previous_period');
    const [comparisonFromDate, setComparisonFromDate] = useState(initialComparisonFromDate);
    const [comparisonToDate, setComparisonToDate] = useState(initialComparisonToDate);
    const [comparisonPickerOpen, setComparisonPickerOpen] = useState(false);
    const [comparisonPickerMonth, setComparisonPickerMonth] = useState(
        parseInputDate(initialComparisonToDate).getMonth()
    );
    const [comparisonPickerYear, setComparisonPickerYear] = useState(
        parseInputDate(initialComparisonToDate).getFullYear()
    );
    const [draftComparisonRange, setDraftComparisonRange] = useState<ComparisonDateRange>({
        start: parseInputDate(initialComparisonFromDate),
        end: parseInputDate(initialComparisonToDate),
    });
    const [aboutOpen, setAboutOpen] = useState(false);
    const [aboutInteracting, setAboutInteracting] = useState(false);
    const [resultsPage, setResultsPage] = useState(1);
    const [resultsPageSize, setResultsPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [coverageLoading, setCoverageLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [coverageError, setCoverageError] = useState<string>();

    const loadReport = useCallback(async (
        selectedRange: ReportRange,
        selectedFrom: string,
        selectedTo: string,
        selectedComparison: ComparisonPeriod,
        selectedComparisonFrom: string,
        selectedComparisonTo: string
    ) => {
        if (selectedRange === 'custom' && (!selectedFrom || !selectedTo || selectedFrom > selectedTo)) {
            setError('Choose a valid custom date range.');
            return;
        }

        if (selectedComparison === 'custom') {
            if (!selectedComparisonFrom || !selectedComparisonTo || selectedComparisonFrom > selectedComparisonTo) {
                setError('Choose a valid custom comparison period.');
                return;
            }

            const selectedPeriodDays = selectedRange === 'custom'
                ? getInputDateRangeDays(selectedFrom, selectedTo)
                : Number(selectedRange);
            const comparisonPeriodDays = getInputDateRangeDays(
                selectedComparisonFrom,
                selectedComparisonTo
            );
            if (comparisonPeriodDays !== selectedPeriodDays) {
                setError(`Choose a comparison period that is also ${selectedPeriodDays} days long.`);
                return;
            }
        }

        setLoading(true);
        setError(undefined);
        try {
            const response = await agent.Reports.searchActivity(selectedRange === 'custom'
                ? {
                    from: selectedFrom,
                    to: selectedTo,
                    comparison: selectedComparison,
                    comparisonFrom: selectedComparison === 'custom' ? selectedComparisonFrom : undefined,
                    comparisonTo: selectedComparison === 'custom' ? selectedComparisonTo : undefined,
                    limit: 250,
                }
                : {
                    days: Number(selectedRange),
                    comparison: selectedComparison,
                    comparisonFrom: selectedComparison === 'custom' ? selectedComparisonFrom : undefined,
                    comparisonTo: selectedComparison === 'custom' ? selectedComparisonTo : undefined,
                    limit: 250,
                });
            setReport(response);
            setAppliedRange(selectedRange);
            setAppliedComparison(selectedComparison);
        } catch (requestError) {
            setError(await getApiErrorMessage(requestError, 'Unable to load reports.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadReport(
            '30',
            initialFromDate,
            initialToDate,
            'previous_period',
            initialComparisonFromDate,
            initialComparisonToDate
        );
    }, [loadReport]);

    const loadCoverageHealth = useCallback(async () => {
        setCoverageLoading(true);
        setCoverageError(undefined);
        try {
            setCoverageHealth(await agent.Reports.fitmentCoverage(250));
        } catch (requestError) {
            setCoverageError(await getApiErrorMessage(requestError, 'Unable to load fitment coverage health.'));
        } finally {
            setCoverageLoading(false);
        }
    }, []);

    useEffect(() => {
        if (
            selectedReport === 'fitment_coverage_health'
            && !coverageHealth
            && !coverageLoading
            && !coverageError
        ) {
            void loadCoverageHealth();
        }
    }, [coverageError, coverageHealth, coverageLoading, loadCoverageHealth, selectedReport]);

    const topSearches = report?.topSearches ?? [];
    const coverageOpportunities = [...topSearches]
        .filter((search) => search.currentMappedProductCount <= 5)
        .sort((left, right) => {
            const leftHasNoResults = left.currentMappedProductCount === 0;
            const rightHasNoResults = right.currentMappedProductCount === 0;

            if (leftHasNoResults !== rightHasNoResults) {
                return leftHasNoResults ? -1 : 1;
            }

            return right.searchCount - left.searchCount;
        });
    const noResultOpportunities = coverageOpportunities.filter((search) => search.currentMappedProductCount === 0);
    const lowCoverageOpportunities = coverageOpportunities.filter((search) => search.currentMappedProductCount > 0);
    const noResultSearchDemand = noResultOpportunities.reduce(
        (total, search) => total + search.searchCount,
        0
    );
    const comparisonDataComplete = isComparisonDataComplete(report);
    const demandTrendRows = [...topSearches]
        .sort((left, right) => {
            if (!report?.comparisonEnabled || !comparisonDataComplete) {
                return right.searchCount - left.searchCount;
            }

            const changeDifference = (right.searchCount - right.previousSearchCount)
                - (left.searchCount - left.previousSearchCount);
            return changeDifference || right.searchCount - left.searchCount;
        });
    const newVehicleSearches = report?.comparisonEnabled && comparisonDataComplete
        ? demandTrendRows.filter((search) =>
            search.searchCount > 0 && search.previousSearchCount === 0
        ).length
        : '—';
    const currentPeriodChange = (report?.totalSearches ?? 0) - (report?.previousTotalSearches ?? 0);
    const selectedSearchRows = selectedReport === 'coverage_opportunities'
        ? coverageOpportunities
        : selectedReport === 'search_demand_trends'
            ? demandTrendRows
            : topSearches;
    const coverageHealthRows = coverageHealth?.vehicles ?? [];
    const chartDays = Math.min(30, getInclusiveDayCount(report));
    const dailyBreakdownRows = buildDailyBreakdownRows(report, chartDays);
    const activeRowCount = selectedReport === 'fitment_coverage_health'
        ? coverageHealthRows.length
        : selectedReport === 'daily_search_volume'
            ? dailyBreakdownRows.length
        : selectedSearchRows.length;
    const selectedRangeLabel = rangeOptions.find((option) => option.value === appliedRange)?.label ?? 'Selected range';
    const selectedComparisonLabel = comparisonOptions.find(
        (option) => option.value === appliedComparison
    )?.label ?? 'Comparison period';
    const supportsComparison = comparisonReportKinds.has(selectedReport);
    const resultsPageCount = Math.max(1, Math.ceil(activeRowCount / resultsPageSize));
    const safeResultsPage = Math.min(resultsPage, resultsPageCount);
    const resultsStartIndex = (safeResultsPage - 1) * resultsPageSize;
    const visibleSearchRows = selectedSearchRows.slice(resultsStartIndex, resultsStartIndex + resultsPageSize);
    const visibleCoverageHealthRows = coverageHealthRows.slice(resultsStartIndex, resultsStartIndex + resultsPageSize);
    const visibleDailyBreakdownRows = dailyBreakdownRows.slice(
        resultsStartIndex,
        resultsStartIndex + resultsPageSize
    );
    const visibleResultsStart = activeRowCount === 0 ? 0 : resultsStartIndex + 1;
    const visibleResultsEnd = Math.min(resultsStartIndex + resultsPageSize, activeRowCount);
    const visibleDailySearchTotal = dailyBreakdownRows.reduce(
        (total, day) => total + (day.currentDataAvailable ? day.currentSearches : 0),
        0
    );
    const visibleDailyComparisonTotal = dailyBreakdownRows.reduce(
        (total, day) => total + (
            day.comparisonDataAvailable
                ? day.comparisonSearches ?? 0
                : 0
        ),
        0
    );
    const currentDailyDataComplete = dailyBreakdownRows.every(
        (day) => day.currentDataAvailable
    );
    const comparisonDailyDataComplete = dailyBreakdownRows.every(
        (day) => day.comparisonDataAvailable !== false
    );
    const fitmentCoverageRate = coverageHealth?.totalVehicles
        ? (coverageHealth.vehiclesWithMappings / coverageHealth.totalVehicles) * 100
        : 0;

    useEffect(() => {
        setResultsPage(1);
    }, [coverageHealth, report, resultsPageSize, selectedReport]);

    const getDefaultCustomComparisonRange = (): ComparisonDateRange => {
        const selectedPeriodDays = range === 'custom'
            ? getInputDateRangeDays(fromDate, toDate)
            : Number(range);
        const currentPeriodStart = range === 'custom'
            ? fromDate
            : shiftInputDate(formatDateOnly(report?.to ?? initialToDate), -(selectedPeriodDays - 1));
        const defaultComparisonTo = shiftInputDate(currentPeriodStart, -1);
        const defaultComparisonFrom = shiftInputDate(
            defaultComparisonTo,
            -(selectedPeriodDays - 1)
        );

        return {
            start: parseInputDate(defaultComparisonFrom),
            end: parseInputDate(defaultComparisonTo),
        };
    };

    const openComparisonPicker = (rangeToEdit?: ComparisonDateRange) => {
        const nextRange = rangeToEdit ?? {
            start: parseInputDate(comparisonFromDate),
            end: parseInputDate(comparisonToDate),
        };
        setDraftComparisonRange(nextRange);
        setComparisonPickerMonth(nextRange.end.getMonth());
        setComparisonPickerYear(nextRange.end.getFullYear());
        setComparisonPickerOpen(true);
    };

    const closeComparisonPicker = () => {
        setDraftComparisonRange({
            start: parseInputDate(comparisonFromDate),
            end: parseInputDate(comparisonToDate),
        });
        setComparisonPickerOpen(false);
    };

    const handleComparisonChange = (value: string) => {
        const nextComparison = value as ComparisonPeriod;
        setComparison(nextComparison);

        if (nextComparison !== 'custom') {
            setComparisonPickerOpen(false);
            return;
        }

        const defaultRange = getDefaultCustomComparisonRange();
        const defaultFrom = toInputDate(defaultRange.start);
        const defaultTo = toInputDate(defaultRange.end);
        setComparisonFromDate(defaultFrom);
        setComparisonToDate(defaultTo);
        openComparisonPicker(defaultRange);
    };

    const expectedComparisonDays = range === 'custom'
        ? getInputDateRangeDays(fromDate, toDate)
        : Number(range);
    const draftComparisonDays = getInputDateRangeDays(
        toInputDate(draftComparisonRange.start),
        toInputDate(draftComparisonRange.end)
    );
    const customComparisonIsValid = expectedComparisonDays > 0
        && draftComparisonDays === expectedComparisonDays;
    const currentPeriodStart = range === 'custom'
        ? fromDate
        : shiftInputDate(
            formatDateOnly(report?.to ?? initialToDate),
            -(Math.max(1, expectedComparisonDays) - 1)
        );
    const latestComparisonDate = parseInputDate(shiftInputDate(currentPeriodStart, -1));

    const applyComparisonPicker = () => {
        if (!customComparisonIsValid) return;
        setComparisonFromDate(toInputDate(draftComparisonRange.start));
        setComparisonToDate(toInputDate(draftComparisonRange.end));
        setComparisonPickerOpen(false);
    };

    const returnToComparisonOptions = () => {
        closeComparisonPicker();
        setComparison('previous_period');
    };

    const selectedReportLabel = reportDefinitions.find(
        (definition) => definition.value === selectedReport
    )?.label ?? 'Report';
    const aboutReport = (
        <Popover
            active={aboutOpen || aboutInteracting}
            activator={(
                <span
                    className="pf-report-about-trigger"
                    onMouseEnter={() => setAboutInteracting(true)}
                    onMouseLeave={() => setAboutInteracting(false)}
                >
                    <Button
                        icon={InfoIcon}
                        onClick={() => setAboutOpen((open) => !open)}
                        onFocus={() => setAboutInteracting(true)}
                        onBlur={() => setAboutInteracting(false)}
                        accessibilityLabel="About this report"
                        ariaExpanded={aboutOpen || aboutInteracting}
                    />
                </span>
            )}
            onClose={() => {
                setAboutOpen(false);
                setAboutInteracting(false);
            }}
            preferredPosition="below"
            preferredAlignment="right"
            sectioned
        >
            <div className="pf-report-about-popover">
                <BlockStack gap="150">
                    <Text as="h2" variant="headingSm">
                        {selectedReportLabel}
                    </Text>
                    <Text as="p" variant="bodySm">
                        Data is aggregated at the store level and does not include customer, session, or device information.
                        Search trends compare equal-length periods. Coverage reports use current product mappings.
                    </Text>
                </BlockStack>
            </div>
        </Popover>
    );

    const exportDisabled = selectedReport === 'fitment_coverage_health'
        ? coverageHealthRows.length === 0
        : selectedReport === 'coverage_opportunities'
            ? coverageOpportunities.length === 0
            : selectedReport === 'daily_search_volume'
                ? !report?.dailyCounts.length
                : !report?.topSearches.length;
    const exportReport = () => {
        if (selectedReport === 'coverage_opportunities') {
            if (!report) return;
            downloadCoverageCsv(report, coverageOpportunities);
            return;
        }

        if (selectedReport === 'search_demand_trends') {
            if (!report) return;
            downloadDemandTrendsCsv(report, demandTrendRows);
            return;
        }

        if (selectedReport === 'fitment_coverage_health') {
            if (!coverageHealth) return;
            downloadFitmentCoverageCsv(coverageHealth, coverageHealthRows);
            return;
        }

        if (!report) return;
        if (selectedReport === 'daily_search_volume') {
            downloadDailyVolumeCsv(report);
            return;
        }

        downloadTopSearchesCsv(report);
    };

    return (
        <Page
            title="Reports"
            subtitle="Understand storefront search demand and fitment coverage."
            primaryAction={(
                <InlineStack gap="200" blockAlign="center">
                    <Button
                        variant="primary"
                        onClick={exportReport}
                        disabled={exportDisabled}
                    >
                        Export CSV
                    </Button>
                    {aboutReport}
                </InlineStack>
            )}
        >
            <div>
                <BlockStack gap="400">
                    {selectedReport !== 'fitment_coverage_health' && error && (
                        <Banner tone="critical">{error}</Banner>
                    )}
                    {selectedReport === 'fitment_coverage_health' && coverageError && (
                        <Banner tone="critical">{coverageError}</Banner>
                    )}

                    <Card>
                        <BlockStack gap="300">
                            <Text as="h2" variant="headingMd">Report filters</Text>
                            <InlineStack gap="300" blockAlign="end" wrap>
                                <div style={{ minWidth: 260 }}>
                                    <Select
                                        label="Report"
                                        options={reportOptions}
                                        value={selectedReport}
                                        onChange={(value) => setSelectedReport(value as ReportKind)}
                                    />
                                </div>
                                {selectedReport === 'fitment_coverage_health' ? (
                                    <>
                                        <div style={{ minWidth: 200 }}>
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                Current mapping snapshot
                                            </Text>
                                        </div>
                                        <Button
                                            onClick={loadCoverageHealth}
                                            loading={coverageLoading}
                                        >
                                            Refresh
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div style={{ minWidth: 200 }}>
                                            <Select
                                                label="Date range"
                                                options={rangeOptions}
                                                value={range}
                                                onChange={(value) => setRange(value as ReportRange)}
                                            />
                                        </div>
                                        {range === 'custom' && (
                                            <>
                                                <div style={{ minWidth: 170 }}>
                                                    <TextField
                                                        label="From"
                                                        type="date"
                                                        autoComplete="off"
                                                        value={fromDate}
                                                        max={toDate}
                                                        onChange={setFromDate}
                                                    />
                                                </div>
                                                <div style={{ minWidth: 170 }}>
                                                    <TextField
                                                        label="To"
                                                        type="date"
                                                        autoComplete="off"
                                                        value={toDate}
                                                        min={fromDate}
                                                        max={initialToDate}
                                                        onChange={setToDate}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        {supportsComparison && comparison !== 'custom' && (
                                            <div style={{ minWidth: 250 }}>
                                                <Select
                                                    label="Compare to"
                                                    options={comparisonOptions}
                                                    value={comparison}
                                                    onChange={handleComparisonChange}
                                                />
                                            </div>
                                        )}
                                        {supportsComparison && comparison === 'custom' && (
                                            <div style={{ minWidth: 310 }}>
                                                <BlockStack gap="100">
                                                    <Text as="span" variant="bodySm">
                                                        Compare to
                                                    </Text>
                                                    <Popover
                                                        active={comparisonPickerOpen}
                                                        activator={(
                                                            <Button
                                                                icon={CalendarIcon}
                                                                disclosure={comparisonPickerOpen ? 'up' : 'down'}
                                                                onClick={() => openComparisonPicker()}
                                                                fullWidth
                                                                textAlign="left"
                                                                accessibilityLabel={`Custom comparison period: ${formatComparisonRangeLabel(comparisonFromDate, comparisonToDate)}`}
                                                            >
                                                                {formatComparisonRangeLabel(
                                                                    comparisonFromDate,
                                                                    comparisonToDate
                                                                )}
                                                            </Button>
                                                        )}
                                                        onClose={closeComparisonPicker}
                                                        preferredPosition="below"
                                                        preferredAlignment="left"
                                                        fluidContent
                                                        autofocusTarget="none"
                                                    >
                                                        <div className="pf-comparison-date-picker">
                                                            <div className="pf-comparison-date-picker__header">
                                                                <Button
                                                                    variant="plain"
                                                                    icon={ArrowLeftIcon}
                                                                    onClick={returnToComparisonOptions}
                                                                >
                                                                    Back
                                                                </Button>
                                                            </div>

                                                            <Divider />

                                                            <div className="pf-comparison-date-picker__body">
                                                                <div className="pf-comparison-date-picker__range">
                                                                    <div>
                                                                        <Text as="span" variant="bodySm" tone="subdued">
                                                                            From
                                                                        </Text>
                                                                        <Text as="p" fontWeight="medium">
                                                                            {formatPickerDate(draftComparisonRange.start)}
                                                                        </Text>
                                                                    </div>
                                                                    <span aria-hidden="true">→</span>
                                                                    <div>
                                                                        <Text as="span" variant="bodySm" tone="subdued">
                                                                            To
                                                                        </Text>
                                                                        <Text as="p" fontWeight="medium">
                                                                            {formatPickerDate(draftComparisonRange.end)}
                                                                        </Text>
                                                                    </div>
                                                                </div>

                                                                <DatePicker
                                                                    month={comparisonPickerMonth}
                                                                    year={comparisonPickerYear}
                                                                    selected={draftComparisonRange}
                                                                    allowRange
                                                                    disableDatesAfter={latestComparisonDate}
                                                                    onChange={setDraftComparisonRange}
                                                                    onMonthChange={(month, year) => {
                                                                        setComparisonPickerMonth(month);
                                                                        setComparisonPickerYear(year);
                                                                    }}
                                                                />

                                                                <Text
                                                                    as="p"
                                                                    variant="bodySm"
                                                                    tone={customComparisonIsValid ? 'subdued' : 'critical'}
                                                                >
                                                                    {customComparisonIsValid
                                                                        ? `${draftComparisonDays} days selected, matching the report period.`
                                                                        : `Select exactly ${expectedComparisonDays} days to match the report period. ${draftComparisonDays} currently selected.`}
                                                                </Text>
                                                            </div>

                                                            <Divider />

                                                            <div className="pf-comparison-date-picker__footer">
                                                                <Button onClick={closeComparisonPicker}>Cancel</Button>
                                                                <Button
                                                                    variant="primary"
                                                                    disabled={!customComparisonIsValid}
                                                                    onClick={applyComparisonPicker}
                                                                >
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Popover>
                                                </BlockStack>
                                            </div>
                                        )}
                                        <Button
                                            variant="primary"
                                            onClick={() => loadReport(
                                                range,
                                                fromDate,
                                                toDate,
                                                supportsComparison ? comparison : 'previous_period',
                                                comparisonFromDate,
                                                comparisonToDate
                                            )}
                                            loading={loading}
                                        >
                                            Apply
                                        </Button>
                                    </>
                                )}
                            </InlineStack>
                        </BlockStack>
                    </Card>

                    {(selectedReport === 'fitment_coverage_health'
                        ? coverageLoading && !coverageHealth
                        : loading && !report) ? (
                        <Card>
                            <InlineStack align="center">
                                <Spinner accessibilityLabel="Loading reports" />
                            </InlineStack>
                        </Card>
                    ) : selectedReport === 'fitment_coverage_health' && !coverageHealth ? null : (
                        <>
                        {selectedReport === 'daily_search_volume' || selectedReport === 'top_vehicle_searches' ? (
                            <Card>
                                <BlockStack gap="400">
                                <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                    <BlockStack gap="100">
                                        <Text as="h2" variant="headingMd">Search overview</Text>
                                        {report && (
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                {formatDisplayDate(report.from)} to {formatDisplayDate(report.to)}
                                            </Text>
                                        )}
                                    </BlockStack>
                                    <InlineStack gap="200" wrap>
                                        <Badge tone="info">{selectedRangeLabel}</Badge>
                                        {report?.comparisonEnabled && (
                                            <Badge>{selectedComparisonLabel}</Badge>
                                        )}
                                    </InlineStack>
                                </InlineStack>

                                <div className="pf-report-summary-metrics">
                                    <ReportSummaryMetric
                                        label="Total searches"
                                        value={report?.totalSearches ?? 0}
                                        comparisonValue={report?.previousTotalSearches ?? 0}
                                        showComparison={Boolean(
                                            report?.comparisonEnabled && comparisonDataComplete
                                        )}
                                    />
                                    <ReportSummaryMetric
                                        label="Unique vehicle searches"
                                        value={report?.uniqueSearchCount ?? 0}
                                        comparisonValue={report?.previousUniqueSearchCount ?? 0}
                                        showComparison={Boolean(
                                            report?.comparisonEnabled && comparisonDataComplete
                                        )}
                                    />
                                    <ReportSummaryMetric
                                        label="Searches today"
                                        value={report?.searchesToday ?? 0}
                                    />
                                </div>

                                <Divider />

                                {selectedReport === 'daily_search_volume' ? (
                                    <BlockStack gap="400">
                                        <BlockStack gap="100">
                                            <Text as="h3" variant="headingSm">Daily search volume</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                The most recent {chartDays} day{chartDays === 1 ? '' : 's'} in the selected range
                                            </Text>
                                            <div style={{ paddingTop: 12 }}>
                                                <SearchActivityChart
                                                    report={report}
                                                    days={chartDays}
                                                    showComparison={report?.comparisonEnabled}
                                                    variant="line"
                                                />
                                            </div>
                                        </BlockStack>

                                        <Divider />

                                        <BlockStack gap="300">
                                            <BlockStack gap="100">
                                                <Text as="h3" variant="headingSm">Daily breakdown</Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Exact search totals for each plotted day.
                                                </Text>
                                            </BlockStack>

                                            {report?.comparisonEnabled && !comparisonDailyDataComplete && (
                                                <Banner tone="info">
                                                    Search tracking for this store begins on {formatDisplayDate(report.dataAvailableFrom ?? undefined)}.
                                                    Earlier comparison dates are marked as no data and are not treated as zero-search days.
                                                </Banner>
                                            )}

                                            {report?.comparisonEnabled ? (
                                                <DataTable
                                                    columnContentTypes={['text', 'numeric', 'text', 'numeric', 'text']}
                                                    headings={['Date', 'Searches', 'Comparison date', 'Comparison searches', 'Change']}
                                                    rows={[
                                                        [
                                                            <strong>{formatDisplayDate(dailyBreakdownRows[0]?.currentDate)}–{formatDisplayDate(dailyBreakdownRows[dailyBreakdownRows.length - 1]?.currentDate)}</strong>,
                                                            <strong>
                                                                {formatCount(visibleDailySearchTotal)}
                                                                {!currentDailyDataComplete ? ' (partial)' : ''}
                                                            </strong>,
                                                            <strong>{formatDisplayDate(dailyBreakdownRows[0]?.comparisonDate)}–{formatDisplayDate(dailyBreakdownRows[dailyBreakdownRows.length - 1]?.comparisonDate)}</strong>,
                                                            <strong>
                                                                {formatCount(visibleDailyComparisonTotal)}
                                                                {!comparisonDailyDataComplete ? ' (partial)' : ''}
                                                            </strong>,
                                                            <Badge>
                                                                {currentDailyDataComplete && comparisonDailyDataComplete
                                                                    ? formatPercentChange(visibleDailySearchTotal, visibleDailyComparisonTotal)
                                                                    : 'Partial data'}
                                                            </Badge>,
                                                        ],
                                                        ...visibleDailyBreakdownRows.map((day) => {
                                                            const comparisonSearches = day.comparisonSearches ?? 0;
                                                            const dataComparable = day.currentDataAvailable
                                                                && day.comparisonDataAvailable;
                                                            const change = dataComparable
                                                                ? getPercentChange(day.currentSearches, comparisonSearches)
                                                                : undefined;
                                                            return [
                                                                formatDisplayDate(day.currentDate),
                                                                day.currentDataAvailable
                                                                    ? formatCount(day.currentSearches)
                                                                    : 'No data',
                                                                formatDisplayDate(day.comparisonDate),
                                                                day.comparisonDataAvailable
                                                                    ? formatCount(comparisonSearches)
                                                                    : 'No data',
                                                                <Badge tone={
                                                                    change != null && change > 0
                                                                        ? 'success'
                                                                        : change != null && change < 0
                                                                            ? 'attention'
                                                                            : undefined
                                                                }>
                                                                    {dataComparable
                                                                        ? formatPercentChange(day.currentSearches, comparisonSearches)
                                                                        : 'No data'}
                                                                </Badge>,
                                                            ];
                                                        }),
                                                    ]}
                                                />
                                            ) : (
                                                <DataTable
                                                    columnContentTypes={['text', 'numeric']}
                                                    headings={['Date', 'Searches']}
                                                    rows={[
                                                        [
                                                            <strong>{formatDisplayDate(dailyBreakdownRows[0]?.currentDate)}–{formatDisplayDate(dailyBreakdownRows[dailyBreakdownRows.length - 1]?.currentDate)}</strong>,
                                                            <strong>{formatCount(visibleDailySearchTotal)}</strong>,
                                                        ],
                                                        ...visibleDailyBreakdownRows.map((day) => [
                                                            formatDisplayDate(day.currentDate),
                                                            day.currentDataAvailable
                                                                ? formatCount(day.currentSearches)
                                                                : 'No data',
                                                        ]),
                                                    ]}
                                                />
                                            )}

                                            {dailyBreakdownRows.length > 0 && (
                                                <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                                    <Text as="p" variant="bodySm" tone="subdued">
                                                        Showing {formatCount(visibleResultsStart)}-{formatCount(visibleResultsEnd)} of {formatCount(dailyBreakdownRows.length)} days
                                                    </Text>
                                                    <InlineStack gap="300" blockAlign="center" wrap>
                                                        <div style={{ minWidth: 155 }}>
                                                            <Select
                                                                label="Rows per page"
                                                                labelInline
                                                                options={[
                                                                    { label: '10', value: '10' },
                                                                    { label: '25', value: '25' },
                                                                    { label: '30', value: '30' },
                                                                ]}
                                                                value={String(resultsPageSize)}
                                                                onChange={(value) => setResultsPageSize(Number(value))}
                                                            />
                                                        </div>
                                                        <Pagination
                                                            type="table"
                                                            accessibilityLabel="Daily search breakdown pagination"
                                                            accessibilityLabels={{
                                                                previous: 'Previous daily results page',
                                                                next: 'Next daily results page',
                                                            }}
                                                            hasPrevious={safeResultsPage > 1}
                                                            hasNext={safeResultsPage < resultsPageCount}
                                                            onPrevious={() => setResultsPage((current) => Math.max(1, current - 1))}
                                                            onNext={() => setResultsPage((current) => Math.min(resultsPageCount, current + 1))}
                                                            label={`Page ${safeResultsPage} of ${resultsPageCount}`}
                                                        />
                                                    </InlineStack>
                                                </InlineStack>
                                            )}
                                        </BlockStack>
                                    </BlockStack>
                                ) : (
                                    <BlockStack gap="300">
                                        <BlockStack gap="100">
                                            <Text as="h3" variant="headingSm">Top vehicle searches</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                Ranked by total searches in the selected range. Up to 250 vehicle combinations are available for review and export.
                                            </Text>
                                        </BlockStack>

                                        {report?.comparisonEnabled && !comparisonDataComplete && (
                                            <Banner tone="info">
                                                The comparison period begins before search tracking started on {formatDisplayDate(report.dataAvailableFrom ?? undefined)}.
                                                Comparison totals are partial, so change values are withheld.
                                            </Banner>
                                        )}

                                        {topSearches.length === 0 ? (
                                            <Text as="p" tone="subdued">No vehicle searches were recorded in this range.</Text>
                                        ) : (
                                            report?.comparisonEnabled ? (
                                                <DataTable
                                                    columnContentTypes={['numeric', 'text', 'numeric', 'numeric', 'text', 'text']}
                                                    headings={[
                                                        'Rank',
                                                        'Vehicle',
                                                        'Current',
                                                        comparisonDataComplete ? 'Comparison' : 'Comparison (partial)',
                                                        'Change',
                                                        'Last searched',
                                                    ]}
                                                    rows={visibleSearchRows.map((search, index) => {
                                                        const change = search.searchCount - search.previousSearchCount;
                                                        return [
                                                            resultsStartIndex + index + 1,
                                                            <VehicleProductsLink search={search} />,
                                                            formatCount(search.searchCount),
                                                            formatCount(search.previousSearchCount),
                                                            <Badge tone={
                                                                comparisonDataComplete
                                                                    ? change > 0
                                                                        ? 'success'
                                                                        : change < 0
                                                                            ? 'attention'
                                                                            : undefined
                                                                    : undefined
                                                            }>
                                                                {comparisonDataComplete
                                                                    ? `${change > 0 ? '+' : ''}${formatCount(change)}`
                                                                    : 'Partial data'}
                                                            </Badge>,
                                                            formatDisplayDate(search.lastSearchedOn),
                                                        ];
                                                    })}
                                                />
                                            ) : (
                                                <DataTable
                                                    columnContentTypes={['numeric', 'text', 'numeric', 'text']}
                                                    headings={['Rank', 'Vehicle', 'Searches', 'Last searched']}
                                                    rows={visibleSearchRows.map((search, index) => [
                                                        resultsStartIndex + index + 1,
                                                        <VehicleProductsLink search={search} />,
                                                        formatCount(search.searchCount),
                                                        formatDisplayDate(search.lastSearchedOn),
                                                    ])}
                                                />
                                            )
                                        )}

                                        {topSearches.length > 0 && (
                                            <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Showing {formatCount(visibleResultsStart)}-{formatCount(visibleResultsEnd)} of {formatCount(topSearches.length)}
                                                </Text>
                                                <InlineStack gap="300" blockAlign="center" wrap>
                                                    <div style={{ minWidth: 155 }}>
                                                        <Select
                                                            label="Rows per page"
                                                            labelInline
                                                            options={[
                                                                { label: '10', value: '10' },
                                                                { label: '25', value: '25' },
                                                                { label: '50', value: '50' },
                                                            ]}
                                                            value={String(resultsPageSize)}
                                                            onChange={(value) => setResultsPageSize(Number(value))}
                                                        />
                                                    </div>
                                                    <Pagination
                                                        type="table"
                                                        accessibilityLabel="Vehicle search results pagination"
                                                        accessibilityLabels={{
                                                            previous: 'Previous results page',
                                                            next: 'Next results page',
                                                        }}
                                                        hasPrevious={safeResultsPage > 1}
                                                        hasNext={safeResultsPage < resultsPageCount}
                                                        onPrevious={() => setResultsPage((current) => Math.max(1, current - 1))}
                                                        onNext={() => setResultsPage((current) => Math.min(resultsPageCount, current + 1))}
                                                        label={`Page ${safeResultsPage} of ${resultsPageCount}`}
                                                    />
                                                </InlineStack>
                                            </InlineStack>
                                        )}
                                    </BlockStack>
                                )}
                                </BlockStack>
                            </Card>
                        ) : selectedReport === 'coverage_opportunities' ? (
                            <Card>
                                <BlockStack gap="400">
                                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                        <BlockStack gap="100">
                                            <Text as="h2" variant="headingMd">Coverage opportunities</Text>
                                            {report && (
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {formatDisplayDate(report.from)} to {formatDisplayDate(report.to)}
                                                </Text>
                                            )}
                                        </BlockStack>
                                        <Badge tone="info">{selectedRangeLabel}</Badge>
                                    </InlineStack>

                                    <Text as="p" tone="subdued">
                                        Find high-demand vehicle searches with no current product mappings or five
                                        mapped products or fewer.
                                    </Text>

                                    <div className="pf-report-summary-metrics">
                                        <ReportSummaryMetric
                                            label="Vehicles with no mappings"
                                            value={noResultOpportunities.length}
                                        />
                                        <ReportSummaryMetric
                                            label="No-mapping search demand"
                                            value={noResultSearchDemand}
                                        />
                                        <ReportSummaryMetric
                                            label="Low-coverage vehicles"
                                            value={lowCoverageOpportunities.length}
                                        />
                                    </div>

                                    <Divider />

                                    <BlockStack gap="300">
                                        <BlockStack gap="100">
                                            <Text as="h3" variant="headingSm">Prioritized opportunities</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                Unmapped vehicles appear first, followed by low-coverage vehicles ranked
                                                by search demand. Coverage is calculated when the report loads for up to
                                                250 of the most searched vehicle combinations.
                                            </Text>
                                        </BlockStack>

                                        {coverageOpportunities.length === 0 ? (
                                            <Banner tone="success">
                                                No unmapped or low-coverage vehicle searches were found in this range.
                                            </Banner>
                                        ) : (
                                            <DataTable
                                                columnContentTypes={['numeric', 'text', 'numeric', 'text', 'text']}
                                                headings={['Priority', 'Vehicle', 'Search demand', 'Current mappings', 'Last searched']}
                                                rows={visibleSearchRows.map((search, index) => [
                                                    resultsStartIndex + index + 1,
                                                    <VehicleProductsLink search={search} />,
                                                    formatCount(search.searchCount),
                                                    search.currentMappedProductCount === 0
                                                        ? <Badge tone="critical">No mappings</Badge>
                                                        : <Badge tone="warning">{`${formatCount(search.currentMappedProductCount)} products`}</Badge>,
                                                    formatDisplayDate(search.lastSearchedOn),
                                                ])}
                                            />
                                        )}

                                        {coverageOpportunities.length > 0 && (
                                            <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Showing {formatCount(visibleResultsStart)}-{formatCount(visibleResultsEnd)} of {formatCount(coverageOpportunities.length)}
                                                </Text>
                                                <InlineStack gap="300" blockAlign="center" wrap>
                                                    <div style={{ minWidth: 155 }}>
                                                        <Select
                                                            label="Rows per page"
                                                            labelInline
                                                            options={[
                                                                { label: '10', value: '10' },
                                                                { label: '25', value: '25' },
                                                                { label: '50', value: '50' },
                                                            ]}
                                                            value={String(resultsPageSize)}
                                                            onChange={(value) => setResultsPageSize(Number(value))}
                                                        />
                                                    </div>
                                                    <Pagination
                                                        type="table"
                                                        accessibilityLabel="Coverage opportunity results pagination"
                                                        accessibilityLabels={{
                                                            previous: 'Previous opportunities page',
                                                            next: 'Next opportunities page',
                                                        }}
                                                        hasPrevious={safeResultsPage > 1}
                                                        hasNext={safeResultsPage < resultsPageCount}
                                                        onPrevious={() => setResultsPage((current) => Math.max(1, current - 1))}
                                                        onNext={() => setResultsPage((current) => Math.min(resultsPageCount, current + 1))}
                                                        label={`Page ${safeResultsPage} of ${resultsPageCount}`}
                                                    />
                                                </InlineStack>
                                            </InlineStack>
                                        )}
                                    </BlockStack>
                                </BlockStack>
                            </Card>
                        ) : selectedReport === 'search_demand_trends' ? (
                            <Card>
                                <BlockStack gap="400">
                                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                        <BlockStack gap="100">
                                            <Text as="h2" variant="headingMd">Search demand trends</Text>
                                            {report && (
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {formatDisplayDate(report.from)} to {formatDisplayDate(report.to)}
                                                </Text>
                                            )}
                                        </BlockStack>
                                        <InlineStack gap="200" wrap>
                                            <Badge tone="info">{selectedRangeLabel}</Badge>
                                            {report?.comparisonEnabled && (
                                                <Badge>{selectedComparisonLabel}</Badge>
                                            )}
                                        </InlineStack>
                                    </InlineStack>

                                    <Text as="p" tone="subdued">
                                        {report?.comparisonEnabled
                                            ? comparisonDataComplete
                                                ? `Compare this period with ${formatDisplayDate(report.previousFrom)} to ${formatDisplayDate(report.previousTo)} to spot growing demand and newly searched vehicles.`
                                                : `The selected comparison begins before search tracking started on ${formatDisplayDate(report.dataAvailableFrom ?? undefined)}. Recorded comparison counts are shown, but changes are withheld.`
                                            : 'Search activity for the selected period. Choose a comparison period to see demand changes and newly searched vehicles.'}
                                    </Text>

                                    <div className="pf-report-summary-metrics">
                                        <ReportSummaryMetric
                                            label="Current searches"
                                            value={report?.totalSearches ?? 0}
                                            comparisonValue={report?.previousTotalSearches ?? 0}
                                            showComparison={Boolean(
                                                report?.comparisonEnabled && comparisonDataComplete
                                            )}
                                        />
                                        <ReportSummaryMetric
                                            label={report?.comparisonEnabled ? 'Net search change' : 'Unique vehicle searches'}
                                            value={report?.comparisonEnabled
                                                ? comparisonDataComplete
                                                    ? `${currentPeriodChange > 0 ? '+' : ''}${formatCount(currentPeriodChange)}`
                                                    : '—'
                                                : report?.uniqueSearchCount ?? 0}
                                        />
                                        <ReportSummaryMetric
                                            label={report?.comparisonEnabled ? 'New searched vehicles' : 'Searches today'}
                                            value={report?.comparisonEnabled
                                                ? newVehicleSearches
                                                : report?.searchesToday ?? 0}
                                        />
                                    </div>

                                    {report?.comparisonEnabled && (
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            {!comparisonDataComplete
                                                ? `Comparison data is partial for ${formatDisplayDate(report.previousFrom)} to ${formatDisplayDate(report.previousTo)}.`
                                                : report.previousTotalSearches === 0
                                                    ? `No searches were recorded during the comparison period, ${formatDisplayDate(report.previousFrom)} to ${formatDisplayDate(report.previousTo)}.`
                                                    : `Search volume is ${formatPercentChange(report.totalSearches, report.previousTotalSearches)} versus ${formatDisplayDate(report.previousFrom)} to ${formatDisplayDate(report.previousTo)}.`}
                                        </Text>
                                    )}

                                    <Divider />

                                    <BlockStack gap="300">
                                        <BlockStack gap="100">
                                            <Text as="h3" variant="headingSm">Vehicle demand changes</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                {report?.comparisonEnabled
                                                    ? comparisonDataComplete
                                                        ? 'Vehicles are ranked by the increase in searches. “New” means the vehicle was searched in this period but not in the selected comparison period. Up to 250 of the current period’s most-searched vehicles are compared.'
                                                        : 'Vehicles are ranked by current searches because the selected comparison period contains incomplete historical data.'
                                                    : 'Vehicles are ranked by searches in the selected period. Choose a comparison period to see changes.'}
                                            </Text>
                                        </BlockStack>

                                        {demandTrendRows.length === 0 ? (
                                            <Text as="p" tone="subdued">
                                                No vehicle searches were recorded in this range.
                                            </Text>
                                        ) : (
                                            report?.comparisonEnabled ? (
                                                <DataTable
                                                    columnContentTypes={['numeric', 'text', 'numeric', 'numeric', 'text', 'text']}
                                                    headings={[
                                                        'Rank',
                                                        'Vehicle',
                                                        'Current',
                                                        comparisonDataComplete ? 'Comparison' : 'Comparison (partial)',
                                                        'Change',
                                                        'Last searched',
                                                    ]}
                                                    rows={visibleSearchRows.map((search, index) => {
                                                        const change = search.searchCount - search.previousSearchCount;
                                                        const changeBadge = !comparisonDataComplete
                                                            ? <Badge>Partial data</Badge>
                                                            : search.previousSearchCount === 0
                                                                ? <Badge tone="info">New</Badge>
                                                                : change > 0
                                                                    ? <Badge tone="success">{`+${formatCount(change)}`}</Badge>
                                                                    : change < 0
                                                                        ? <Badge tone="attention">{formatCount(change)}</Badge>
                                                                        : <Badge>No change</Badge>;

                                                        return [
                                                            resultsStartIndex + index + 1,
                                                            <VehicleProductsLink search={search} />,
                                                            formatCount(search.searchCount),
                                                            formatCount(search.previousSearchCount),
                                                            changeBadge,
                                                            formatDisplayDate(search.lastSearchedOn),
                                                        ];
                                                    })}
                                                />
                                            ) : (
                                                <DataTable
                                                    columnContentTypes={['numeric', 'text', 'numeric', 'text']}
                                                    headings={['Rank', 'Vehicle', 'Searches', 'Last searched']}
                                                    rows={visibleSearchRows.map((search, index) => [
                                                        resultsStartIndex + index + 1,
                                                        <VehicleProductsLink search={search} />,
                                                        formatCount(search.searchCount),
                                                        formatDisplayDate(search.lastSearchedOn),
                                                    ])}
                                                />
                                            )
                                        )}

                                        {demandTrendRows.length > 0 && (
                                            <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Showing {formatCount(visibleResultsStart)}-{formatCount(visibleResultsEnd)} of {formatCount(demandTrendRows.length)}
                                                </Text>
                                                <InlineStack gap="300" blockAlign="center" wrap>
                                                    <div style={{ minWidth: 155 }}>
                                                        <Select
                                                            label="Rows per page"
                                                            labelInline
                                                            options={[
                                                                { label: '10', value: '10' },
                                                                { label: '25', value: '25' },
                                                                { label: '50', value: '50' },
                                                            ]}
                                                            value={String(resultsPageSize)}
                                                            onChange={(value) => setResultsPageSize(Number(value))}
                                                        />
                                                    </div>
                                                    <Pagination
                                                        type="table"
                                                        accessibilityLabel="Search demand trend results pagination"
                                                        accessibilityLabels={{
                                                            previous: 'Previous trend results page',
                                                            next: 'Next trend results page',
                                                        }}
                                                        hasPrevious={safeResultsPage > 1}
                                                        hasNext={safeResultsPage < resultsPageCount}
                                                        onPrevious={() => setResultsPage((current) => Math.max(1, current - 1))}
                                                        onNext={() => setResultsPage((current) => Math.min(resultsPageCount, current + 1))}
                                                        label={`Page ${safeResultsPage} of ${resultsPageCount}`}
                                                    />
                                                </InlineStack>
                                            </InlineStack>
                                        )}
                                    </BlockStack>
                                </BlockStack>
                            </Card>
                        ) : (
                            <Card>
                                <BlockStack gap="400">
                                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                        <BlockStack gap="100">
                                            <Text as="h2" variant="headingMd">Fitment coverage health</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                Current active catalog snapshot
                                            </Text>
                                        </BlockStack>
                                        <Badge tone="info">Current snapshot</Badge>
                                    </InlineStack>

                                    <Text as="p" tone="subdued">
                                        Measure how much of the active vehicle catalog has product mappings and identify
                                        vehicles that need coverage work.
                                    </Text>

                                    <div className="pf-report-summary-metrics pf-report-summary-metrics--four">
                                        <ReportSummaryMetric
                                            label="Active vehicles"
                                            value={coverageHealth?.totalVehicles ?? 0}
                                        />
                                        <ReportSummaryMetric
                                            label="Vehicles without mappings"
                                            value={coverageHealth?.vehiclesWithoutMappings ?? 0}
                                        />
                                        <ReportSummaryMetric
                                            label="Low-coverage vehicles"
                                            value={coverageHealth?.lowCoverageVehicles ?? 0}
                                        />
                                        <ReportSummaryMetric
                                            label="Coverage rate"
                                            value={`${Math.round(fitmentCoverageRate)}%`}
                                        />
                                    </div>

                                    {coverageHealth && (
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            {fitmentCoverageRate.toFixed(1)}% of active vehicles have at least one mapped
                                            product, with an average of {coverageHealth.averageMappedProducts.toFixed(1)}{' '}
                                            mapped products among covered vehicles.
                                        </Text>
                                    )}

                                    <Divider />

                                    <BlockStack gap="300">
                                        <BlockStack gap="100">
                                            <Text as="h3" variant="headingSm">Vehicles needing attention</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                This review list includes unmapped vehicles first, followed by vehicles
                                                with five mapped products or fewer. Summary metrics cover the full active
                                                catalog.
                                            </Text>
                                        </BlockStack>

                                        {(coverageHealth?.totalVehicles ?? 0) === 0 ? (
                                            <Banner tone="info">
                                                No active vehicles are available in the current fitment catalog.
                                            </Banner>
                                        ) : coverageHealthRows.length === 0 ? (
                                            <Banner tone="success">
                                                Every active vehicle currently has more than five mapped products.
                                            </Banner>
                                        ) : (
                                            <DataTable
                                                columnContentTypes={['numeric', 'text', 'numeric', 'text']}
                                                headings={['Priority', 'Vehicle', 'Current mappings', 'Coverage status']}
                                                rows={visibleCoverageHealthRows.map((vehicle, index) => [
                                                    resultsStartIndex + index + 1,
                                                    <VehicleProductsLink search={vehicle} />,
                                                    formatCount(vehicle.currentMappedProductCount),
                                                    vehicle.currentMappedProductCount === 0
                                                        ? <Badge tone="critical">No mappings</Badge>
                                                        : <Badge tone="warning">Low coverage</Badge>,
                                                ])}
                                            />
                                        )}

                                        {coverageHealthRows.length > 0 && (
                                            <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Showing {formatCount(visibleResultsStart)}-{formatCount(visibleResultsEnd)} of {formatCount(coverageHealthRows.length)}
                                                </Text>
                                                <InlineStack gap="300" blockAlign="center" wrap>
                                                    <div style={{ minWidth: 155 }}>
                                                        <Select
                                                            label="Rows per page"
                                                            labelInline
                                                            options={[
                                                                { label: '10', value: '10' },
                                                                { label: '25', value: '25' },
                                                                { label: '50', value: '50' },
                                                            ]}
                                                            value={String(resultsPageSize)}
                                                            onChange={(value) => setResultsPageSize(Number(value))}
                                                        />
                                                    </div>
                                                    <Pagination
                                                        type="table"
                                                        accessibilityLabel="Fitment coverage health results pagination"
                                                        accessibilityLabels={{
                                                            previous: 'Previous coverage health page',
                                                            next: 'Next coverage health page',
                                                        }}
                                                        hasPrevious={safeResultsPage > 1}
                                                        hasNext={safeResultsPage < resultsPageCount}
                                                        onPrevious={() => setResultsPage((current) => Math.max(1, current - 1))}
                                                        onNext={() => setResultsPage((current) => Math.min(resultsPageCount, current + 1))}
                                                        label={`Page ${safeResultsPage} of ${resultsPageCount}`}
                                                    />
                                                </InlineStack>
                                            </InlineStack>
                                        )}
                                    </BlockStack>
                                </BlockStack>
                            </Card>
                        )}
                        </>
                    )}
                </BlockStack>
            </div>
        </Page>
    );
};

export default ReportsPage;
